import { ProgressSpinner } from "primereact/progressspinner";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { JournalVoucherStyles } from "./JournalVoucherShared";

const TYPE_TITLES = {
  journal: "Journal",
  payment: "Payment",
  receipt: "Receipt",
  contra: "Contra",
};

const TYPE_PREFIX = {
  journal: "JV",
  payment: "PV",
  receipt: "RV",
  contra: "CV",
};

const money = (value) =>
  parseFloat(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const amount = (value) => (parseFloat(value || 0) > 0 ? money(value) : "");

// "2026-09-24" as "24 Sep 2026", split as text for the same timezone reason
// voucherNumber gives below.
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const displayDate = (value) => {
  const [year, month, day] = String(value || "")
    .slice(0, 10)
    .split("-");
  if (!year || !month || !day) return value || "";
  return `${day} ${MONTHS[Number(month) - 1] || month} ${year}`;
};

// Vouchers raised since the numbering change store their full number, so it is
// printed as issued. Older ones hold a bare serial, which is dressed up the same
// way for display: type prefix, then the year and month of the voucher date. The
// date is split as text, since parsing it as a Date would shift the month across
// a timezone boundary.
const voucherNumber = (voucher) => {
  if (!voucher || !voucher.serial_number) return "";

  const stored = String(voucher.serial_number);
  if (/^[A-Z]{2}-\d{2}-\d{2}-\d+$/.test(stored)) return stored;

  const [year = "", month = ""] = String(voucher.date || "").split("-");
  const serial = stored.replace(/^0+/, "") || "0";
  const prefix = TYPE_PREFIX[voucher.type] || "JV";

  return [prefix, year.slice(-2), month, serial].filter(Boolean).join("-");
};

const accountLabel = (account) =>
  account ? account.code + " - " + account.name : "";

// The subsidiary ledger is identified by its own code, and named after whichever
// entity the line points at.
const subsidiaryLedger = (line) => {
  const ledger = line.sub_category;
  if (!ledger) return "";

  let name = ledger.name;
  if (name === "new") name = ledger.new_name;
  else if (name === "hr" && line.hr) name = line.hr.name;
  else if (name === "assets" && line.asset) name = line.asset.name;
  else if (name === "supplier" && line.supplier) name = line.supplier.name;
  else if (name === "distributor" && line.distributor)
    name = line.distributor.name;

  return [ledger.code, name].filter(Boolean).join(" - ");
};

// Lines are printed under their control account: a bold parent row carrying the
// group's totals, then the accounts actually posted to. An account with no
// parent heads its own group.
const groupByParent = (items) => {
  const groups = [];
  const index = {};

  (items || []).forEach((line) => {
    const account = line.chart_of_account;
    const head = account?.parent || account;
    const key = head?.id ?? "none";

    if (!index[key]) {
      index[key] = { head, lines: [], debit: 0, credit: 0 };
      groups.push(index[key]);
    }

    index[key].lines.push(line);
    index[key].debit += parseFloat(line.debit) || 0;
    index[key].credit += parseFloat(line.credit) || 0;
  });

  return groups;
};

const Show = () => {
  const [receiveData, setReceiveData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    Http.get("/journal-voucher/" + id)
      .then((res) => {
        setReceiveData(res.data || {});
      })
      .catch((e) => {
        console.error("Fetch error:", e);
        setError("Could not load this voucher. Please refresh and try again.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const print = () => {
    window.print();
  };

  const items = receiveData.items || [];
  const groups = groupByParent(items);
  const serial = voucherNumber(receiveData);
  const title = TYPE_TITLES[receiveData.type] || "Journal";
  // The details table breaks a posting down by subsidiary ledger and
  // reference, so it only concerns the lines that carry one. A voucher with
  // none is printed without the section rather than with an empty table.
  const detailLines = items.filter(
    (line) => line.sub_category || line.reference || line.existing_reference,
  );

  const debitTotal = items.reduce(
    (sum, line) => sum + (parseFloat(line.debit) || 0),
    0,
  );
  const creditTotal = items.reduce(
    (sum, line) => sum + (parseFloat(line.credit) || 0),
    0,
  );
  const balanced =
    Math.round(debitTotal * 100) === Math.round(creditTotal * 100);

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <JournalVoucherStyles />
        <style>
          {`
            @media print {
                body * { visibility: hidden; }
                .no-print { display: none !important; }
                #printable-content, #printable-content * { visibility: visible; }
                #printable-content {
                  position: absolute; left: 0; top: 0; width: 100%;
                  padding: 0; box-shadow: none; border: 0;
                }
                .am-pagebody { padding: 0; }
                .am-mainpanel { margin-top: 0; }
                @page { margin: 12mm; }
            }
            #printable-content {
              background: #fff;
              border: 1px solid #e6e8f0;
              border-radius: 10px;
              box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
              padding: 32px 36px 40px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .vch-head {
              display: flex;
              align-items: flex-end;
              justify-content: space-between;
              gap: 24px;
              margin-top: 18px;
              padding-bottom: 14px;
              border-bottom: 2px solid #2a2962;
            }
            .vch-title {
              font-size: 1.6rem;
              font-weight: 700;
              color: #2a2962;
              margin: 0;
              letter-spacing: 0.01em;
            }
            .vch-subtitle {
              margin-top: 2px;
              font-size: 0.82rem;
              color: #8b90a5;
              text-transform: uppercase;
              letter-spacing: 0.08em;
            }
            .vch-meta {
              display: grid;
              grid-template-columns: auto auto;
              column-gap: 14px;
              row-gap: 3px;
              font-size: 0.9rem;
              white-space: nowrap;
            }
            .vch-meta dt {
              font-weight: 600;
              font-size: 0.72rem;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              color: #8b90a5;
              align-self: center;
            }
            .vch-meta dd {
              margin: 0;
              font-weight: 600;
              color: #2a2962;
              text-align: right;
            }
            .vch-meta .vch-serial {
              font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
            }
            .vch-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 22px;
            }
            .vch-table th, .vch-table td {
              border: 1px solid #e3e5ec;
              padding: 8px 12px;
              font-size: 0.88rem;
              color: #374151;
            }
            .vch-table thead th {
              background: #2a2962;
              border-color: #2a2962;
              color: #fff;
              font-size: 0.72rem;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.04em;
            }
            .vch-table .num {
              text-align: right;
              white-space: nowrap;
              font-variant-numeric: tabular-nums;
            }
            .vch-group > td {
              background: #f2f3f9;
              font-weight: 700;
              color: #2a2962;
            }
            .vch-line > td:nth-child(2) { padding-left: 26px; }
            .vch-total > td {
              border-top: 2px solid #2a2962;
              font-weight: 700;
              color: #2a2962;
              background: #f7f8fc;
            }
            .vch-code {
              font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
              font-size: 0.82rem;
            }
            .vch-section-title {
              text-align: left;
              background: #f2f3f9 !important;
              color: #2a2962 !important;
              border-color: #e3e5ec !important;
              font-size: 0.8rem !important;
            }
            .vch-balance {
              display: block;
              margin-top: 2px;
              font-size: 0.7rem;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            .vch-balance.is-ok { color: #1e7e34; }
            .vch-balance.is-off { color: #e42320; }
            .vch-remarks {
              margin-top: 18px;
              padding: 12px 14px;
              border-left: 3px solid #2a2962;
              background: #f7f8fc;
              font-size: 0.9rem;
              color: #4b5563;
            }
            .vch-remarks strong {
              display: block;
              margin-bottom: 2px;
              font-size: 0.72rem;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              color: #8b90a5;
            }
            .vch-signatures {
              display: flex;
              justify-content: space-between;
              gap: 28px;
              margin-top: 90px;
              page-break-inside: avoid;
            }
            .vch-signatures div {
              flex: 1;
              text-align: center;
              font-weight: 700;
              font-size: 0.78rem;
              letter-spacing: 0.06em;
              color: #1f2937;
              padding-top: 8px;
              border-top: 1px solid #9ca3af;
            }
          `}
        </style>

        <div className="am-pagetitle no-print">
          <div>
            <h5 className="am-title mb-1">
              {title} Voucher{" "}
              {serial && <span className="jv-serial">{serial}</span>}
            </h5>
            <p className="text-muted mb-0 tx-12">
              {receiveData.date
                ? `Dated ${displayDate(receiveData.date)} · ${items.length} lines`
                : "Voucher details"}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Link to="/journal-voucher" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Back to List
            </Link>
            {receiveData.id && (
              <Link
                to={`/edit-journal-voucher/${receiveData.id}`}
                className="btn btn-outline-secondary"
              >
                <i className="fa fa-pencil mg-r-6"></i>Edit
              </Link>
            )}
            <button
              className="btn btn-primary"
              onClick={print}
              disabled={loading || !receiveData.id}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          {loading ? (
            <div className="jv-panel">
              <div className="jv-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : error ? (
            <div className="jv-panel">
              <div className="jv-empty">
                <i className="fa fa-exclamation-triangle"></i>
                <h6 style={{ color: "#e42320" }}>{error}</h6>
                <button
                  className="btn btn-outline-secondary btn-sm mg-t-10"
                  onClick={fetchData}
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div id="printable-content">
              <div className="text-center">
                <img
                  src="/ldl-logo.png"
                  alt="Limerick Distribution Ltd."
                  style={{ width: "26%", maxWidth: "320px" }}
                />
              </div>

              <div className="vch-head">
                <div>
                  <h2 className="vch-title">{title} Voucher</h2>
                  <div className="vch-subtitle">Double-entry posting</div>
                </div>
                <dl className="vch-meta mb-0">
                  <dt>Voucher No</dt>
                  <dd className="vch-serial">{serial}</dd>
                  <dt>Date</dt>
                  <dd>{displayDate(receiveData.date)}</dd>
                  <dt>Type</dt>
                  <dd>{title}</dd>
                </dl>
              </div>

              <table className="vch-table">
                <thead>
                  <tr>
                    <th style={{ width: "13%" }}>Acc-Code</th>
                    <th style={{ width: "32%" }}>Head Of Accounts</th>
                    <th className="num" style={{ width: "14%" }}>
                      Dr
                    </th>
                    <th className="num" style={{ width: "14%" }}>
                      Cr
                    </th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group, groupIndex) => (
                    <React.Fragment key={groupIndex}>
                      <tr className="vch-group">
                        <td className="vch-code">{group.head?.code}</td>
                        <td>{group.head?.name}</td>
                        <td className="num">{amount(group.debit)}</td>
                        <td className="num">{amount(group.credit)}</td>
                        <td></td>
                      </tr>
                      {group.lines.map((line) => (
                        <tr className="vch-line" key={line.id}>
                          <td className="vch-code">
                            {line.chart_of_account?.code}
                          </td>
                          <td>{line.chart_of_account?.name}</td>
                          <td className="num">{amount(line.debit)}</td>
                          <td className="num">{amount(line.credit)}</td>
                          <td>{line.remarks}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}

                  <tr className="vch-total">
                    <td></td>
                    <td className="num">
                      Total Amount
                      <span
                        className={`vch-balance ${
                          balanced ? "is-ok" : "is-off"
                        }`}
                      >
                        {balanced
                          ? "Debits equal credits"
                          : `Out of balance by ${money(
                              Math.abs(debitTotal - creditTotal),
                            )}`}
                      </span>
                    </td>
                    <td className="num">{money(receiveData.total_amount)}</td>
                    <td className="num">{money(receiveData.total_amount)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>

              {detailLines.length > 0 && (
                <table className="vch-table">
                  <thead>
                    <tr>
                      <th className="vch-section-title" colSpan="9">
                        Details Transaction
                      </th>
                    </tr>
                    <tr>
                      <th>Chart Of Accounts</th>
                      <th>Subsidiary Ledger</th>
                      <th>Bill No</th>
                      <th>Bill Date</th>
                      <th>Reference</th>
                      <th>Existing Reference</th>
                      <th className="num">Debit</th>
                      <th className="num">Credit</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailLines.map((line) => (
                      <tr key={line.id}>
                        <td>{accountLabel(line.chart_of_account)}</td>
                        <td>{subsidiaryLedger(line)}</td>
                        <td>{line.bill_number}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {displayDate(line.bill_date)}
                        </td>
                        <td>{line.reference}</td>
                        <td>{line.existing_reference}</td>
                        <td className="num">{amount(line.debit)}</td>
                        <td className="num">{amount(line.credit)}</td>
                        <td>{line.remarks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {receiveData.remarks && (
                <p className="vch-remarks">
                  <strong>Remarks</strong>
                  {receiveData.remarks}
                </p>
              )}

              <div className="vch-signatures">
                <div>PREPARED BY</div>
                <div>CHECKED BY</div>
                <div>AUTHORIZED BY</div>
                <div>APPROVED BY</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Show;
